import React from "react";
// ============================================================================
// Templates Page
// ============================================================================
// Purpose: Main page for ad template management
// Route: /templates
// ============================================================================

import { useState, useEffect } from 'react';
import { TemplateList } from '@/components/templates/TemplateList';
import TemplateForm from '@/components/templates/TemplateForm';
import { useTemplates } from '@/hooks/useTemplates';
import { useAdAccount } from '@/contexts/AdAccountContext';
import { AdTemplate } from '@/types/template';

type PageView = 'list' | 'create' | 'edit';

export default function TemplatesPage() {
  const [currentView, setCurrentView] = useState<PageView>('list');
  const [editingTemplateId, setEditingTemplateId] = useState<string | undefined>();
  const [editingTemplate, setEditingTemplate] = useState<AdTemplate | undefined>();
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const { fetchTemplate } = useTemplates();
  const { selectedAdAccountId } = useAdAccount();

  // Fetch template data when editing
  useEffect(() => {
    const loadTemplate = async () => {
      if (editingTemplateId && currentView === 'edit') {
        setLoadingTemplate(true);
        const template = await fetchTemplate(editingTemplateId);
        setEditingTemplate(template || undefined);
        setLoadingTemplate(false);
      } else {
        setEditingTemplate(undefined);
      }
    };

    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTemplateId, currentView]);

  const handleCreateTemplate = () => {
    setEditingTemplateId(undefined);
    setEditingTemplate(undefined);
    setCurrentView('create');
  };

  const handleEditTemplate = (templateId: string) => {
    setEditingTemplateId(templateId);
    setCurrentView('edit');
  };

  const handleSave = () => {
    setCurrentView('list');
    setEditingTemplateId(undefined);
    setEditingTemplate(undefined);
  };

  const handleCancel = () => {
    setCurrentView('list');
    setEditingTemplateId(undefined);
    setEditingTemplate(undefined);
  };

  return (
    <div className="container mx-auto py-8">
      {currentView === 'list' && (
        <TemplateList
          onCreateTemplate={handleCreateTemplate}
          onEditTemplate={handleEditTemplate}
          adAccountFilter={selectedAdAccountId || undefined}
        />
      )}

      {currentView === 'create' && (
        <TemplateForm
          onSuccess={handleSave}
        />
      )}

      {currentView === 'edit' && loadingTemplate && (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading template...</div>
        </div>
      )}

      {currentView === 'edit' && !loadingTemplate && editingTemplate && (
        <TemplateForm
          template={editingTemplate}
          onSuccess={handleSave}
        />
      )}
    </div>
  );
}
